import client from "services/ngage/api-client";
import DataPage from "types/DataPage";
import {SponsorshipTier, SponsorshipTierData} from "types/SponsorshipTier";
import deserializeSponsorshipTier from "services/ngage/deserializers/sponsorship-tier";
import {queryString} from "services/string";
import {snakeKeys} from "services/array";
import DataResource from "types/DataResource";

type SponsorshipTierFilters = {
    search?: string
    page?: number
    perPage?: number
}

/**
 * Get all sponsorship tiers (paginated)
 */
const all = async (page: number = 1, perPage: number = 100) => {
    const query = queryString({page, per_page: perPage})
    const response = await client.get<DataPage<SponsorshipTier> | null>(`/sponsorship-tiers?${query}`)

    if (response.data) {
        response.data.data = response.data.data.map(deserializeSponsorshipTier)
    }

    return response.data
}

/**
 * Get filtered sponsorship plans (paginated)
 */
const filtered = async (filters: SponsorshipTierFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<SponsorshipTier> | null>('/sponsorship-tiers', {params})).data

        if (page) page.data = page.data.map(deserializeSponsorshipTier)

        return page
    } catch (error) {
        console.error("Unable to fetch sponsorship tiers", error)
        return null
    }
}

/**
 * Get a single sponsorship tier
 *
 * @param {number} id
 */
const get = async (id: number) => {
    return deserializeSponsorshipTier(
        (await client.get<SponsorshipTier>(`/sponsorship-tiers/${id}`)).data
    )
}

/**
 * Store a new sponsorship tier
 *
 * @param data
 */
const store = async (data: SponsorshipTierData) => {
    return deserializeSponsorshipTier(
        (await client.post<SponsorshipTier>('/sponsorship-tiers', data)).data
    )
}

/**
 * Update an existing sponsorship tier
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: SponsorshipTierData) => {
    return deserializeSponsorshipTier(
        (await client.put<SponsorshipTier>(`/sponsorship-tiers/${id}`, data)).data
    )
}

/**
 * Permanently delete an sponsorship tier
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    return (await client.delete<null>(`/sponsorship-tiers/${id}`)).status === 204
}

const sponsorshipTiers = {
    all,
    filtered,
    get,
    store,
    update,
    destroy
}

export default sponsorshipTiers;